import api from '@/api'

import type { PortalApiResponse } from '@/types/apiResponses'
import type { Portal } from '@/types/portal'
import { useAppStore } from '@/stores/app'
import { useAppCookies } from '@/composables/useCookies'
import { idpLogout } from '@/api/idp'
import { apiClient } from '@/api/client'
import { TokenHelper } from '@deltax/idp-api-client'
import EmbedApiClient from '@/utils/embedApiClient'
import type { BusinessProfile } from '@/types/businessProfile'
import type { User } from '@/types/user'

let sessionRecoveryInFlight: Promise<void> | null = null

class AppService {
  private embedApiClient = new EmbedApiClient(import.meta.env.VITE_DX_API_URL)

  getPortalData = async (portalDomain: string): Promise<Portal> => {
    const portalData: PortalApiResponse = await api.get(
      'portals',
      { domain: portalDomain },
      { headers: { 'X-Bypass-Login': true } },
    )

    return portalData.data[0]
  }

  getCurrentUser = async (): Promise<User> => {
    const response = await api.get<{ data: { user: User; roleIds: number[] } }>('users/me')
    return response.data.user
  }

  getUserData = async (emailAddress: string) => {
    return api.get('users', { email: emailAddress })
  }

  getUserById = async (userId: string) => {
    return api.get('users', { ids: userId })
  }

  getPortalBusinessProfiles = async (
    userId: number | undefined,
    portalId: number | undefined,
  ): Promise<BusinessProfile[]> => {
    const response = await api.get<{ data: BusinessProfile[] }>(
      `portals/${portalId}/users/${userId}/businessProfiles`,
      { userId },
    )

    let businessProfiles = response.data
    businessProfiles = [
      ...new Map(businessProfiles.map((bp: BusinessProfile) => [bp.id, bp])).values(),
    ]
    return businessProfiles
  }

  loginWithEmbedToken = async (
    clientToken: string,
    userId: string,
    brandId: string | undefined,
  ) => {
    return this.embedApiClient.apiMethods.loginWithEmbedToken(clientToken, userId, brandId)
  }

  assignDivisionBrandsToClient = async (
    businessProfileId: number | null,
    brandIds: string[],
    roleId: number,
  ) => {
    const requestData = {
      name: 'Stunnr BE - Client Division',
      brandIds,
      roleId,
      updateUserSettings: true,
    }

    return api.post(`businessProfiles/${businessProfileId}/divisions`, requestData)
  }

  submitCretiveFeedback = async (
    businessProfileId: number | null,
    reaction: number,
    comment: string | null,
    imageUrl: string,
  ): Promise<number> => {
    const requestData = {
      reaction,
      comment,
      type: 1,
      appId: 12,
      imageUrl,
    }
    const response = await api.post<{ id: number }>(
      `businessProfiles/${businessProfileId}/generatedCreativeFeedbacks`,
      requestData,
    )
    return response.id
  }

  updateCreativeFeedback = async (
    businessProfileId: number | null,
    feedbackId: number,
    reaction: number,
    comment: string | null,
  ): Promise<void> => {
    await api.patch(
      `businessProfiles/${businessProfileId}/generatedCreativeFeedbacks/${feedbackId}`,
      { reaction, comment },
    )
  }

  logoutUser = async (): Promise<string> => {
    const appStore = useAppStore()
    appStore.setUser(null)
    appStore.setSelectedBusinessProfileId(null)
    this.removeUserTokens()
    return idpLogout(`${window.location.origin}/login`)
  }

  clearAuthData = async (_clearSessionInRedis: boolean = true) => {
    window.onbeforeunload = null
    const endSessionUrl = await this.logoutUser()
    window.onbeforeunload = null
    window.location.href = endSessionUrl
  }

  recoverSession = async (redirectPath?: string) => {
    if (sessionRecoveryInFlight) {
      return sessionRecoveryInFlight
    }

    sessionRecoveryInFlight = (async () => {
      try {
        this.removeUserTokens()
        const currentPath =
          redirectPath || window.location.pathname + window.location.search + window.location.hash
        const targetRedirect =
          !currentPath || currentPath.startsWith('/login') || currentPath.startsWith('/callback')
            ? '/'
            : currentPath
        const { initiateAuthorize } = await import('@/api/idp')
        await initiateAuthorize(targetRedirect, 'none')
      } catch (error) {
        console.error('Session recovery failed, forcing logout', error)
        await this.clearAuthData(false)
      }
    })().finally(() => {
      sessionRecoveryInFlight = null
    })

    return sessionRecoveryInFlight
  }

  removeUserTokens = () => {
    const { removeCookies } = useAppCookies()

    TokenHelper.removeTokens(apiClient.storage, apiClient.accessTokenStorageKey)
    localStorage.removeItem('accessToken')
    removeCookies('user')
    removeCookies('accessToken')
    removeCookies('accessToken', '/', window.location.hostname)
  }
}

export default AppService
